import { Close } from "@mui/icons-material";
import DOMPurify from "dompurify";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import React, {
  FC,
  Ref,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  getAriaLabelText,
  getAriaRequiredStatus,
  getHelperText,
  removeWhiteSpace,
} from "../../helpers/utils";
import { useMediaQueries } from "../../hooks/useMediaQueries";
import { MuiBox } from "../box/MuiBox";
import { ErrorCrossIcon } from "../errorCrossIcon/ErrorCrossIcon";
import { MuiInfoToolTip } from "../tooltip/infoToolTip/MuiInfoToolTip";
import { MuiTypography } from "../typography/MuiTypography";
import { MuiTextFieldWrapper } from "./MuiTextfield.style";
import { EXTENDED_SPECIAL_CHARS } from "../../helpers/constants";

export type MuiTextFieldProps = TextFieldProps & {
  labelText?: string;
  defaultText?: string;
  successMessage?: string;
  isSuccess?: boolean;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  inputAdornmentPosition?: "start" | "end";
  maxLength?: number;
  autoComplete?: string;
  isMandatory?: boolean;
  innerRef?: Ref<HTMLInputElement>;
  allCaps?: boolean;
  showCloseIcon?: boolean;
  isErrorIconEnabled?: boolean;
  isErrorInfoIconEnabled?: boolean;
  tooltipMessage?: string;
  tooltipHeaderTitle?: string;
  infoMessage?: string;
  inputNumberWithoutDecimal?: boolean;
  isLabelInvisible?: boolean;
  isTextarea?: boolean;
  positiveValuesOnly?: boolean;
  shouldTrimSpaces?: boolean;
  showCharacterCount?: boolean;
  icon?: React.ReactNode;
  shouldDisableInfoIcon?: boolean;
  requiredARIALabel: string;
  helperTextType?: string;
};

interface LabelSectionProps {
  isLabelInvisible?: boolean;
  label: React.ReactNode;
  id?: string;
  disabled?: boolean;
  isMandatory?: boolean;
  requiredARIALabel: string;
  infoMessage?: string;
  shouldDisableInfoIcon?: boolean;
  icon?: React.ReactNode;
}

interface HelperTextProps {
  helperText?: React.ReactNode;
  helperTextType?: string;
  label?: React.ReactNode;
  isErrorIconEnabled: boolean;
  isErrorInfoIconEnabled?: boolean;
  tooltipMessage?: string;
  tooltipHeaderTitle?: string;
}

const LabelSection: FC<LabelSectionProps> = ({
  isLabelInvisible,
  label,
  id,
  disabled,
  isMandatory,
  requiredARIALabel,
  infoMessage,
  shouldDisableInfoIcon,
  icon,
}) => {
  const labelRef = useRef<HTMLLabelElement>(null);
  const [isEllipsis, setIsEllipsis] = useState(false);

  useEffect(() => {
    const el = labelRef.current;
    if (el) {
      const hasEllipsis = el.scrollWidth > el.clientWidth;
      setIsEllipsis(hasEllipsis);
    }
  }, [label]);

  const labelContent = (
    <MuiTypography
      ref={labelRef}
      className={disabled ? "mui-label disabled" : "mui-label"}
      component="label"
      variant="h3"
      htmlFor={id}
    >
      {label}{" "}
      {isMandatory && !disabled && (
        <span className="asterisk" aria-label={requiredARIALabel}>
          *
        </span>
      )}
    </MuiTypography>
  );
  return (
    <MuiBox
      className={`title ${isLabelInvisible ? "invisible" : ""}`}
      id={`${id}-label`}
    >
      {isEllipsis ? (
        <Tooltip title={label} placement="bottom">
          {labelContent}
        </Tooltip>
      ) : (
        labelContent
      )}
      {infoMessage && (
        <MuiBox className="tooltip-container">
          <MuiInfoToolTip
            message={infoMessage}
            placement="top"
            className="tooltip-wrapper"
            shouldDisableInfoIcon={shouldDisableInfoIcon}
          />
        </MuiBox>
      )}
      {icon && <MuiBox className="icon-wrapper">{icon}</MuiBox>}
    </MuiBox>
  );
};

const HelperTextSection: FC<HelperTextProps> = ({
  helperText,
  label,
  isErrorIconEnabled,
  isErrorInfoIconEnabled,
  tooltipMessage,
  tooltipHeaderTitle,
  helperTextType,
}) => {
  return (
    <MuiBox
      role="alert"
      aria-description={getHelperText(helperText as string, label as string)}
      className="error-icon"
      id={`helper-text-${removeWhiteSpace(label as string)}`}
    >
      {isErrorIconEnabled && <ErrorCrossIcon aria-hidden="true" />}

      <div id={`helper-text-${removeWhiteSpace(label as string)}`} aria-hidden>
        {getHelperText(helperText as string, label as string)}
      </div>

      { (isErrorInfoIconEnabled || helperTextType === EXTENDED_SPECIAL_CHARS) && tooltipMessage && (
        <MuiInfoToolTip
          message={DOMPurify.sanitize(tooltipMessage)}
          placement="bottom"
          aria-hidden="true"
          headerTitle={tooltipHeaderTitle}
        />
      )}
    </MuiBox>
  );
};

export const MuiTextField: FC<MuiTextFieldProps> = (
  props: MuiTextFieldProps,
) => {
  const {
    label,
    startAdornment,
    endAdornment,
    inputAdornmentPosition = "start",
    autoComplete = "off",
    maxLength = null,
    isSuccess = false,
    successMessage,
    isMandatory,
    disabled,
    innerRef, // Accept innerRef
    allCaps,
    showCloseIcon = false,
    isErrorIconEnabled = true,
    id,
    helperText,
    helperTextType,
    isErrorInfoIconEnabled,
    tooltipMessage,
    tooltipHeaderTitle,
    infoMessage,
    inputNumberWithoutDecimal = false,
    isLabelInvisible = false,
    positiveValuesOnly = false,
    isTextarea = "",
    shouldTrimSpaces = true,
    showCharacterCount = false,
    icon,
    shouldDisableInfoIcon = false,
    requiredARIALabel = "Required",
    ...rest
  } = props;

  const [count, setCount] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useMediaQueries();

  const onWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    if (e.target instanceof HTMLInputElement && e.target.type === "number") {
      e.target.blur();
    }
  };
  let baseClassName = "mui-text-field";
  if (isTextarea) {
    baseClassName += " mui-textarea";
  }
  const textFieldClassName = isSuccess
    ? "mui-text-field-success"
    : baseClassName;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const numberValue = parseFloat((e.target as HTMLInputElement).value || "0");
    if (
      (rest.type === "number" &&
        (e.key === "e" || e.key === "E" || e.key === "-" || e.key === "+")) ||
      (inputNumberWithoutDecimal && e.key === ".") ||
      (positiveValuesOnly && e.key === "ArrowDown" && numberValue <= 0)
    ) {
      e.preventDefault();
      if (rest.onKeyDown) {
        rest.onKeyDown(e);
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (inputNumberWithoutDecimal) {
      e.preventDefault();

      const pasted = e.clipboardData.getData("text");
      // Keep only digits before the decimal
      let sanitized = pasted.split(".")[0];

      // Remove anything that's not 0–9, including -, +, e, E, spaces, or special characters.
      sanitized = sanitized.replace(/\D/g, "");
      // Call React's onChange to update the value
      rest.onChange?.({
        target: { value: sanitized },
      } as React.ChangeEvent<HTMLInputElement>);
    } else {
      // fallback
      rest.onPaste?.(e);
    }
  };

  const handleFocus = useCallback(() => {
    if (isMobile) {
      containerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [isMobile]);

  return (
    <MuiTextFieldWrapper ref={containerRef}>
      {label && (
        <LabelSection
          isLabelInvisible={isLabelInvisible}
          label={label}
          id={id}
          disabled={disabled}
          isMandatory={isMandatory}
          requiredARIALabel={requiredARIALabel}
          infoMessage={infoMessage}
          shouldDisableInfoIcon={shouldDisableInfoIcon}
          icon={icon}
        />
      )}

      <TextField
        data-testid={id}
        id={id}
        onFocus={handleFocus}
        ref={innerRef} // Attach innerRef to TextField
        className={textFieldClassName}
        autoComplete={autoComplete}
        disabled={disabled}
        helperText={
          (helperText || showCharacterCount) && (
            <MuiBox className="info-box">
              {helperText && (
                <HelperTextSection
                  helperText={helperText}
                  helperTextType={helperTextType}
                  label={label}
                  isErrorIconEnabled={isErrorIconEnabled}
                  isErrorInfoIconEnabled={isErrorInfoIconEnabled}
                  tooltipMessage={tooltipMessage}
                  tooltipHeaderTitle={tooltipHeaderTitle}
                />
              )}
              {showCharacterCount && (
                <MuiTypography className="character-count">
                  {" "}
                  {count} {maxLength && `/ ${maxLength}`}{" "}
                </MuiTypography>
              )}
            </MuiBox>
          )
        }
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onWheel={onWheel}
        {...rest}
        inputProps={{
          "aria-invalid": !!helperText,
          "aria-required": getAriaRequiredStatus(isMandatory, disabled),
          maxLength: maxLength,
          style: { textTransform: allCaps ? "uppercase" : "none" },
          "aria-label": getAriaLabelText(
            label,
            isMandatory,
            disabled,
            requiredARIALabel,
          ),

          ...rest.inputProps,
        }}
        slotProps={{
          input: {
            startAdornment: startAdornment ? (
              <InputAdornment position={inputAdornmentPosition}>
                {startAdornment}
              </InputAdornment>
            ) : undefined,
            endAdornment: endAdornment ? (
              <InputAdornment position={inputAdornmentPosition}>
                {endAdornment}
              </InputAdornment>
            ) : (
              showCloseIcon && (
                <InputAdornment position="end">
                  {rest.value && !disabled ? (
                    <Close
                      onClick={() =>
                        rest.onChange &&
                        rest.onChange({
                          target: { value: "" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />
                  ) : null}
                </InputAdornment>
              )
            ),
            onBlur: (e) => {
              rest.onBlur?.(e);
              if (rest.type !== "number" && rest.value && shouldTrimSpaces) {
                rest.onChange &&
                  rest.onChange({
                    target: { value: e.target.value.trim() },
                  } as React.ChangeEvent<HTMLInputElement>);
              }
            },
            onChange: (e) => {
              rest.onChange?.(e);
              setCount((e.target as HTMLInputElement)?.value.length);
            },
            ...rest.slotProps?.input,
          },
          ...rest.slotProps,
        }}
      />
      {isSuccess && (
        <MuiTypography
          className="mui-label-success"
          component="label"
          variant="h5SemiBold"
          tabIndex={0}
        >
          {successMessage}
        </MuiTypography>
      )}
    </MuiTextFieldWrapper>
  );
};
export type MuiTextFieldWithSearchIconProps = MuiTextFieldProps & {
  searchARIALabel: string;
};

export const MuiTextFieldWithSearchIcon: FC<MuiTextFieldWithSearchIconProps> = (
  props,
) => {
  return (
    <MuiTextField
      {...props}
      aria-label={props.searchARIALabel}
      startAdornment={
        <InputAdornment position="start">
          <SearchIcon />
        </InputAdornment>
      }
    />
  );
};
